import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { fetchConfig } from '@trp-ta-nitro/auth-react';
import type { RootState } from '../store';

export interface TFeature {
  name: string;
  enabled: boolean;
}

export interface TLoadableAppConfig {
  apiKey?: string;
  environment: string;
  version: string;
  entitlementsUrl: string;
  internalLoginUrl: string;
  loginUrl: string;
  logoutUrl: string;
  cancelUrl: string;
  apiDomain: string;
  apiBasePath: string;
  nasuUrl: string;
  contactUsUrl: string;
}

interface IAppConfigState extends TLoadableAppConfig {
  error?: Error | null;
  loadedAt?: number;
  internalApiDomain: string;
}

const initialState: IAppConfigState = {
  environment: 'sandbox',
  version: '1.0.0',
  entitlementsUrl: '',
  internalLoginUrl: '',
  internalApiDomain: '',
  loginUrl: '',
  logoutUrl: '',
  cancelUrl: '',
  apiDomain: '',
  apiBasePath: '/asset-transfer-svc/asset-transfer/api',
  nasuUrl: '',
  contactUsUrl: '',
};

const getConfigUrls = () => {
  if (import.meta.env.DEV) {
    return ['environments/sandbox/envConfig.json'];
  }

  const baseUrl = import.meta.env.VITE_PUBLIC_URL;
  return [baseUrl + 'envConfig.json', baseUrl + 'api-config.json'];
};

// AsyncThunk Action
// =============
export const fetchAndLoadConfig = createAsyncThunk(
  'fetchAndLoadConfig',
  async (_, thunkAPI) => {
    try {
      const data = await fetchConfig(getConfigUrls());
      return data as IAppConfigState;
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  },
);

// Slice
export const appConfigSlice = createSlice({
  name: 'appConfig',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchAndLoadConfig.fulfilled, (state, { payload }) => {
      if (payload) {
        Object.assign(state, {
          ...payload,
          error: null,
          loadedAt: new Date().toDateString(),
        });
      }
    });
    builder.addCase(fetchAndLoadConfig.rejected, (state, action) => {
      state.error = action.error as Error;
    });
  },
});

export function selectApiKey(state: RootState) {
  return state.appConfig.apiKey ?? '';
}

export function selectVersion(state: RootState) {
  return state.appConfig.version;
}

export const selectEnvironment = (state: RootState) => state.appConfig.environment;

export const selectEntitlementsUrl = (state: RootState) =>
  state.appConfig.entitlementsUrl;

export const selectInternalLoginUrl = (state: RootState) =>
  state.appConfig.internalLoginUrl;

export const selectLoginUrl = (state: RootState) => state.appConfig.loginUrl;

export const selectLogoutUrl = (state: RootState) => state.appConfig.logoutUrl;

export const selectAppConfig = (state: RootState) => state.appConfig;

export const selectApiBasePath = (state: RootState) => state.appConfig.apiBasePath;

export const selectApiDomain = (state: RootState) => state.appConfig.apiDomain;

export default appConfigSlice.reducer;
